import { EUserRoles } from '@models/user';

// ─── Types (画面項目一覧 — analyzing-fe.md §2.5, api-integration.md §2.1) ──────

export interface TReportRow {
  assetType: string | null; // [6.2.1] 資産区分 (asset.asset_type)
  assetNo: string | null; // [6.2.2] 資産番号 (asset.asset_no)
  assetName: string | null; // [6.2.4] 資産名称 (asset.asset_name)
  storeCode: string | null; // [6.2.5] 支店 (store.store_code)
  acquisitionDate: string | null; // [6.2.6] 取得日 (asset.acquisition_date) — "YYYYMMDD"
  removeDate: string | null; // 除却年月日 (t_inventory_results.remove_date) — "yyyy/MM/dd"
  acquisitionCostDisplay: string | null; // [6.2.7] 取得価額 — đã format comma
  quantity: number | null; // [6.2.8] 数量 (asset.quantity)
  remarks: string; // [6.2.9] 備考 — rỗng nếu null (không phải "—")
}

export interface TReportPreview {
  inventoryYearMonth: string;
  inventoryYearMonthDisplay: string;
  sessionCode: string;
  storeCode: string;
  storeName: string;
  isAllStores: boolean;
  headerNote: string;
  totalCount: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
  rows: TReportRow[];
}

export interface TPeriodOption {
  sessionCode: string;
  inventoryYearMonth: string;
  inventoryYearMonthDisplay: string;
  statusCode: 'OPEN' | 'IN_PROGRESS' | 'CLOSED';
}

export interface TStoreOption {
  value: string; // composite key: "ALL" hoặc "${storeCode}_${companyCode}" cho admin dropdown
  label: string;
  storeCode?: string; // actual storeCode truyền vào API (undefined khi value = ALL_STORE_VALUE)
  companyCode?: string; // actual companyCode truyền vào API — disambiguate storeCode trùng giữa các company
}

export const ALL_STORE_VALUE = 'ALL';

/**
 * 対象ロール (analyzing-fe.md §2.1): 管理者 chọn bất kỳ store / "全店"; 各店舗 chỉ xem store của mình.
 * EUserRoles chỉ có ADMIN/STORE_OWNER — ADMIN là "管理者"-like, STORE_OWNER là "各店舗".
 */
export function isAdminLikeRole(role: EUserRoles[] | undefined): boolean {
  if (!role || role.length === 0) {
    return false;
  }
  return role.includes(EUserRoles.ADMIN);
}

// ─── Mock data (Phạm vi agent: UI + mock — KHÔNG integrate API thật) ─────────

/**
 * MOCK — Gap G1-a (api-integration.md §2.3): GET /api/v1/reports/inventory/periods chưa có ở BE.
 * Danh sách kỳ 棚卸年月 cho dropdown [4], sort giảm dần theo inventoryYearMonth (kỳ mới nhất trước).
 * BE-PENDING: thay bằng dữ liệu thật khi BE bổ sung endpoint lookup.
 */
export const MOCK_PERIODS: TPeriodOption[] = [
  {
    sessionCode: 'INV202607-OPEN',
    inventoryYearMonth: '202607',
    inventoryYearMonthDisplay: '2026/07',
    statusCode: 'OPEN',
  },
  {
    sessionCode: 'INV202606-CLOSED',
    inventoryYearMonth: '202606',
    inventoryYearMonthDisplay: '2026/06',
    statusCode: 'CLOSED',
  },
  {
    sessionCode: 'INV202605-CLOSED',
    inventoryYearMonth: '202605',
    inventoryYearMonthDisplay: '2026/05',
    statusCode: 'CLOSED',
  },
  {
    sessionCode: 'INV202604-CLOSED',
    inventoryYearMonth: '202604',
    inventoryYearMonthDisplay: '2026/04',
    statusCode: 'CLOSED',
  },
];

/**
 * MOCK — Gap G1-b (api-integration.md §2.4): endpoint lookup store cho dropdown [5] chưa chốt hướng
 * (tái dùng GET /api/v1/get-all-store có điều chỉnh, hay endpoint mới). "全店" luôn đứng đầu;
 * các store còn lại sort tăng theo storeCode. BE-PENDING: thay bằng dữ liệu thật khi BE chốt Gap G1.
 */
export const MOCK_STORES: TStoreOption[] = [
  { value: ALL_STORE_VALUE, label: '全店' },
  { value: '001', label: '001_本店' },
  { value: '002', label: '002_明智店' },
  { value: '003', label: '003_青葉台店' },
  { value: '004', label: '004_緑ヶ丘店' },
  { value: 'S001', label: 'S001_Store 001' },
];

const REPORT_HEADER_NOTE = 'この用紙は記入後に経理4課へ提出すること!';

/**
 * MOCK report rows theo store — dựa trên sample response thật api-integration.md §2.1
 * (`InventoryReportServiceTests.Preview_FieldMapping`) + giá trị ví dụ spec quen thuộc
 * (VD: 20527-001, 417,000 — đã dùng ở VAMS_PC_005 mock).
 * Store "004" cố tình để rỗng để demo MSG-PC007-I01 (該当するデータがありません).
 */
const MOCK_ROWS_BY_STORE: Record<string, TReportRow[]> = {
  S001: [
    {
      assetType: 'IT',
      assetNo: 'AST-S001-001',
      assetName: 'Laptop Dell Latitude',
      storeCode: 'S001',
      acquisitionDate: '20240110',
      removeDate: null,
      acquisitionCostDisplay: '1,500',
      quantity: 1,
      remarks: 'Normal asset with photo and RFID.',
    },
  ],
  '001': [
    {
      assetType: '什器',
      assetNo: '20501-003',
      assetName: 'レジカウンター',
      storeCode: '001',
      acquisitionDate: '20180401',
      removeDate: null,
      acquisitionCostDisplay: '620,000',
      quantity: 2,
      remarks: '',
    },
    {
      assetType: null,
      assetNo: '20501-004',
      assetName: 'バックヤード用棚',
      storeCode: '001',
      acquisitionDate: null,
      removeDate: null,
      acquisitionCostDisplay: null,
      quantity: null,
      remarks: '取得情報未登録',
    },
  ],
  '002': [
    {
      assetType: '器具及び備品',
      assetNo: '20527-001',
      assetName: '青葉センター向け店舗支援カメラ',
      storeCode: '002',
      acquisitionDate: '20151130',
      removeDate: null,
      acquisitionCostDisplay: '417,000',
      quantity: 1,
      remarks: '',
    },
  ],
  '003': [
    {
      assetType: 'IT',
      assetNo: '20533-002',
      assetName: 'POSレジ端末',
      storeCode: '003',
      acquisitionDate: '20220615',
      removeDate: null,
      acquisitionCostDisplay: '298,000',
      quantity: 3,
      remarks: '定期点検対象',
    },
  ],
  '004': [],
};

/** MOCK preview builder — mô phỏng GET /api/v1/reports/inventory/preview (api-integration.md §2.1). */
export function buildMockPreview(
  period: TPeriodOption,
  storeValue: string,
  storeLabel: string
): TReportPreview {
  const isAllStores = storeValue === ALL_STORE_VALUE;
  const rows = isAllStores
    ? Object.values(MOCK_ROWS_BY_STORE).flat()
    : MOCK_ROWS_BY_STORE[storeValue] ?? [];

  return {
    inventoryYearMonth: period.inventoryYearMonth,
    inventoryYearMonthDisplay: period.inventoryYearMonthDisplay,
    sessionCode: period.sessionCode,
    storeCode: storeValue,
    storeName: isAllStores ? '全店' : storeLabel,
    isAllStores,
    headerNote: REPORT_HEADER_NOTE,
    totalCount: rows.length,
    page: 1,
    pageSize: 20,
    hasMore: false,
    rows,
  };
}

// ─── Labels / messages (tiếng Nhật, per skill constant + quy tắc agent) ──────

export const INVENTORY_REPORT_CONSTANT = {
  TITLE: {
    PAGE: '棚卸報告書出力',
    BTN_BACK: '← 戻る',
    BTN_BACK_FOOTER: '戻る',
    BTN_OUTPUT: 'PDF出力',
    OUTPUT_CONFIRM_TITLE: '出力確認',
    BTN_CANCEL: 'キャンセル',
    BTN_OK: 'OK',
  },
  LABEL: {
    OUTPUT_CONDITIONS_SECTION: '出力条件',
    REPORT_PREVIEW_SECTION: '帳票プレビュー',
    INVENTORY_YEAR_MONTH: '棚卸年月',
    STORE: '店舗',
  },
  TABLE_HEADER: {
    ASSET_CATEGORY: '種類',
    ASSET_NO: '資産番号',
    ASSET_NAME: '資産名称',
    STORE: '支店',
    ACQUISITION_DATE: '取得日',
    REMOVE_DATE: '除却年月日',
    ACQUISITION_COST: '取得価額',
    QUANTITY: '数量',
    REMARKS: '備考',
  },
  /**
   * V01/V02/I01 khớp MSG ID trong analyzing-fe.md/api-integration.md.
   * I01 dùng đúng literal text nêu trong analyzing-fe.md §2.4 ("該当するデータがありません").
   * V01/V02/S01/E01/E02/E03: ScreenSpec Part1/Part2 gốc không nằm trong tài liệu phân tích được bàn giao
   * (chỉ có MSG ID + mô tả điều kiện) — nội dung dưới đây là văn phong chuẩn theo các màn hình VAMS khác
   * (VAMS_PC_005/006) cho đến khi BA xác nhận lại text chính thức. Xem UI_VAMS_PC_007.md "Open Items".
   */
  MESSAGE: {
    V01: '棚卸年月を選択してください。',
    V02: '出力対象店舗を選択してください。',
    I01: '該当するデータがありません。',
    S01: '棚卸報告書の出力が完了しました。',
    E01: 'データの取得に失敗しました。再度お試しください。',
    E02: 'PDF出力に失敗しました。再度お試しください。',
    E03: 'システムエラーが発生しました。管理者にお問い合わせください。',
  },
  DIALOG: {
    MESSAGE: '以下の条件で棚卸報告書を出力してもよろしいですか？',
    LABEL_PERIOD: '棚卸年月',
    LABEL_STORE: '出力対象店舗',
  },
};
